'use server'

import { apiRequest, ApiResponse } from '../request'

export interface PrescriptionItem {
    id: number
    prescriptionId: number
    medication: string
    dosage?: string
    frequency?: string
    duration?: string
    instructions?: string
    quantity?: number
    createdAt: string
    updatedAt: string
}

export interface Prescription {
    id: number
    content: string
    observations?: string
    prescriptionDate: string
    validUntil?: string
    clientId: number
    client?: {
        id: number
        name: string
        email?: string
        whatsAppNumber?: string
    }
    professionalId: number
    professional?: {
        id: number
        name: string
        registrationNumber?: string
        councilName?: string
    }
    companyId: number
    company?: {
        id: number
        name: string
        tradingName?: string
        phone?: string
        email?: string
        street?: string
        number?: string
        neighborhood?: string
        city?: string
        state?: string
        zipCode?: string
    }
    items?: PrescriptionItem[]
    signedAt?: string
    signedPdfUrl?: string
    signatureData?: any
    signatureValid?: boolean
    metadata?: any
    createdAt: string
    updatedAt: string
}

export interface CreatePrescriptionItemData {
    medication: string
    dosage?: string
    frequency?: string
    duration?: string
    instructions?: string
    quantity?: number
}

export interface CreatePrescriptionData {
    content: string
    observations?: string
    validUntil?: string
    clientId: number
    professionalId: number
    companyId: number
    items?: CreatePrescriptionItemData[]
    metadata?: any
}

export interface UpdatePrescriptionData extends Partial<CreatePrescriptionData> {}

export async function getPrescriptions(
    companyId?: number
): Promise<ApiResponse<Prescription[]>> {
    const params = companyId ? { companyId } : {}
    return apiRequest<Prescription[]>('prescriptions', { params })
}

export async function getPrescriptionsByCompany(
    companyId: number,
    params?: { page?: number; limit?: number; search?: string },
): Promise<ApiResponse<Prescription[]>> {
    return apiRequest<Prescription[]>(`prescriptions/company/${companyId}`, {
        params: params ?? {},
    })
}

export async function getPrescriptionsByClient(
    clientId: number
): Promise<ApiResponse<Prescription[]>> {
    return apiRequest<Prescription[]>(`prescriptions/client/${clientId}`, {})
}

export async function getPrescriptionsByProfessional(
    professionalId: number
): Promise<ApiResponse<Prescription[]>> {
    return apiRequest<Prescription[]>(
        `prescriptions/professional/${professionalId}`,
        {}
    )
}

export async function getPrescription(
    id: number
): Promise<ApiResponse<Prescription>> {
    return apiRequest<Prescription>(`prescriptions/${id}`, {})
}

export async function getPrescriptionPrintData(
    id: number
): Promise<ApiResponse<Prescription>> {
    return apiRequest<Prescription>(`prescriptions/${id}/print`, {})
}

export async function createPrescription(
    data: CreatePrescriptionData
): Promise<ApiResponse<Prescription>> {
    return apiRequest<Prescription>('prescriptions', { data }, 'post')
}

export async function updatePrescription(
    id: number,
    data: UpdatePrescriptionData
): Promise<ApiResponse<Prescription>> {
    return apiRequest<Prescription>(`prescriptions/${id}`, { data }, 'patch')
}

export async function deletePrescription(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`prescriptions/${id}`, {}, 'delete')
}

export async function addPrescriptionItem(
    prescriptionId: number,
    data: CreatePrescriptionItemData
): Promise<ApiResponse<PrescriptionItem>> {
    return apiRequest<PrescriptionItem>(
        `prescriptions/${prescriptionId}/items`,
        { data },
        'post'
    )
}

export async function updatePrescriptionItem(
    prescriptionId: number,
    itemId: number,
    data: Partial<CreatePrescriptionItemData>
): Promise<ApiResponse<PrescriptionItem>> {
    return apiRequest<PrescriptionItem>(
        `prescriptions/${prescriptionId}/items/${itemId}`,
        { data },
        'patch'
    )
}

export async function deletePrescriptionItem(
    prescriptionId: number,
    itemId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `prescriptions/${prescriptionId}/items/${itemId}`,
        {},
        'delete'
    )
}
